import { ref } from '@vue/composition-api'

/** @typedef {{map: object, order: (string | number)[]}} EventMarkets */

/**
 * Store expanded market IDs for each market group
 * @example
 * {
 *   'Favourites': [1, 2],
 *   'Bet Builder': [3, 4],
 *   'Goals': [5, 6]
 * }
 */
const expandedMarketGroupMap = ref({
  Favourites: [],
  'Bet Builder': []
})

/**
 * Store all market IDs for each market group
 * @example
 * {
 *   'Favourites': [1, 2],
 *   'Bet Builder': [3, 4],
 *   'Goals': [5, 6]
 * }
 */
const marketGroupIdsMap = ref({
  Favourites: [],
  'Bet Builder': []
})

const activeMarketIds = ref([])

export const useExpandedMarkets = () => {
  /**
   * Initialize expanded markets
   * @param {object} options
   * @param {any[]} options.favouriteMarketIds Favourite market IDs
   * @param {EventMarkets} options.markets event.markets
   */
  const initExpandedMarkets = (options = {}) => {
    const { favouriteMarketIds, markets } = options

    const { map, order } = markets
    const maps = order.reduce(
      (tmp, id) => {
        // map[id] may be an array or an object
        const market = Array.isArray(map[id]) ? map[id][0] : map[id]

        // Add market to marketGroupIdsMap
        addToMarketGroupIdsMap({
          marketGroupIdsMap: tmp.marketGroupIdsMap,
          marketId: id,
          marketGroup: market.group
        })

        // Add expanded market to expandedMarketGroupMap
        addToExpandedMarketGroupMap({
          expandedMarketGroupMap: tmp.expandedMarketGroupMap,
          marketId: id,
          marketGroup: market.group,
          expand: market.expand
        })

        return tmp
      },
      {
        expandedMarketGroupMap: {},
        marketGroupIdsMap: {}
      }
    )

    expandedMarketGroupMap.value = {
      ...expandedMarketGroupMap.value,
      ...maps.expandedMarketGroupMap
    }
    marketGroupIdsMap.value = {
      ...marketGroupIdsMap.value,
      ...maps.marketGroupIdsMap
    }

    // Initialize favourite markets group if there are any
    if (favouriteMarketIds?.length) {
      initFavouriteMarkets({ favouriteMarketIds, markets })
    }
  }

  /**
   * Add market to market group ids map
   * @param {object} options
   * @param {object} options.marketGroupIdsMap
   * @param {string} options.marketId market id
   * @param {string} options.marketGroup market group name. e.g. 'Goals' | 'Bet Builder' | 'Favourites'
   */
  const addToMarketGroupIdsMap = (options = {}) => {
    const { marketGroupIdsMap, marketId, marketGroup } = options

    if (marketGroupIdsMap[marketGroup] == null) {
      marketGroupIdsMap[marketGroup] = [marketId]
      return
    }

    if (marketGroupIdsMap[marketGroup].includes(marketId)) {
      return marketGroupIdsMap
    }
    marketGroupIdsMap[marketGroup].push(marketId)

    return marketGroupIdsMap
  }

  /**
   * Add market to expanded market group map
   * @param {object} options
   * @param {object} options.expandedMarketGroupMap
   * @param {string} options.marketId market id
   * @param {string} options.marketGroup market group name. e.g. 'Goals' | 'Bet Builder' | 'Favourites'
   * @param {0 | 1 | boolean} options.expand is market expanded
   */
  const addToExpandedMarketGroupMap = (options = {}) => {
    const { expandedMarketGroupMap, marketId, marketGroup, expand } = options

    if (expandedMarketGroupMap[marketGroup] == null) {
      expandedMarketGroupMap[marketGroup] = []
    }

    if (!expand) {
      return
    }

    if (expandedMarketGroupMap[marketGroup].includes(marketId)) {
      return expandedMarketGroupMap
    }

    expandedMarketGroupMap[marketGroup].push(marketId)

    return expandedMarketGroupMap
  }

  /**
   * Track market expand status
   * @param {object} options
   * @param {string} options.marketGroup market group name. e.g. 'Goals' | 'Bet Builder' | 'Favourites'
   * @param {string} options.marketId market id
   * @param {0 | 1} options.expand is market expanded
   */
  const trackMarketExpandStatus = (options = {}) => {
    const { marketGroup, marketId, expand } = options

    addToExpandedMarketGroupMap({
      expandedMarketGroupMap: expandedMarketGroupMap.value,
      marketId,
      marketGroup,
      expand
    })

    addToMarketGroupIdsMap({
      marketGroupIdsMap: marketGroupIdsMap.value,
      marketId,
      marketGroup
    })
  }

  /**
   * Remove market from all maps
   * @param {object} options
   * @param {string} options.marketGroup market group name. e.g. 'Goals' | 'Bet Builder' | 'Favourites'
   * @param {string} options.marketId market id
   */
  const untrackMarketExpandStatus = (options = {}) => {
    const { marketGroup, marketId } = options

    if (expandedMarketGroupMap.value[marketGroup] != null) {
      expandedMarketGroupMap.value[marketGroup] = expandedMarketGroupMap.value[marketGroup].filter((id) => id !== marketId)
    }

    if (marketGroupIdsMap.value[marketGroup] != null) {
      marketGroupIdsMap.value[marketGroup] = marketGroupIdsMap.value[marketGroup].filter((id) => id !== marketId)
    }
  }

  /**
   * Initialize bet builder market
   * @param {object} options
   * @param {any[]} options.comboMarketGroups
   * @param {boolean} options.updateExistingMarketExpand
   * @param {'replace' | 'append'} options.updateExpandMode `replace` will replace all the existing market; `append` will only add the new market
   * @returns
   */
  const initBetBuilderMarkets = (options = {}) => {
    const { comboMarketGroups, updateExpandMode = 'replace' } = options

    if (!comboMarketGroups?.length) return

    const maps = comboMarketGroups.reduce(
      (tmp, comboMarketGroup) => {
        addToMarketGroupIdsMap({
          marketGroupIdsMap: tmp.marketGroupIdsMap,
          marketId: comboMarketGroup.comboGroupName,
          marketGroup: 'Bet Builder'
        })

        if (updateExpandMode === 'replace') {
          addToExpandedMarketGroupMap({
            expandedMarketGroupMap: tmp.expandedMarketGroupMap,
            marketId: comboMarketGroup.comboGroupName,
            marketGroup: 'Bet Builder',
            expand: comboMarketGroup.isExpand
          })
          return tmp
        }

        if (updateExpandMode === 'append') {
          // If the combo group is already existed, do nothing
          if (marketGroupIdsMap.value['Bet Builder']?.includes(comboMarketGroup.comboGroupName)) {
            return tmp
          }

          addToExpandedMarketGroupMap({
            expandedMarketGroupMap: tmp.expandedMarketGroupMap,
            marketId: comboMarketGroup.comboGroupName,
            marketGroup: 'Bet Builder',
            expand: comboMarketGroup.isExpand
          })
        }

        return tmp
      },
      {
        expandedMarketGroupMap: {},
        marketGroupIdsMap: {}
      }
    )

    expandedMarketGroupMap.value = {
      ...expandedMarketGroupMap.value,
      ...maps.expandedMarketGroupMap
    }

    marketGroupIdsMap.value = {
      ...marketGroupIdsMap.value,
      ...maps.marketGroupIdsMap
    }
  }

  /**
   * Initialize favourite market
   * @param {object} options
   * @param {(string | number)[]} options.favouriteMarketIds
   * @param {{map: object, order: (string | number)[]}} options.markets
   */
  const initFavouriteMarkets = (options = {}) => {
    const { favouriteMarketIds, markets } = options
    const maps = favouriteMarketIds.reduce(
      (tmp, marketId) => {
        const market = Array.isArray(markets.map[marketId]) ? markets.map[marketId][0] : markets.map[marketId]

        addToMarketGroupIdsMap({
          marketGroupIdsMap: tmp.marketGroupIdsMap,
          marketId: marketId,
          marketGroup: 'Favourites'
        })

        addToExpandedMarketGroupMap({
          expandedMarketGroupMap: tmp.expandedMarketGroupMap,
          marketId: marketId,
          marketGroup: 'Favourites',
          expand: market.expand
        })
        return tmp
      },
      {
        expandedMarketGroupMap: {},
        marketGroupIdsMap: {}
      }
    )

    expandedMarketGroupMap.value = {
      ...expandedMarketGroupMap.value,
      ...maps.expandedMarketGroupMap
    }
    marketGroupIdsMap.value = {
      ...marketGroupIdsMap.value,
      ...maps.marketGroupIdsMap
    }
  }

  /**
   * Collapse all markets in a group
   * @param {object} options
   * @param {string} options.marketGroup market group name. e.g. 'Goals' | 'Bet Builder' | 'Favourites'
   */
  const collapseAllMarkets = (options = {}) => {
    const { marketGroup } = options

    expandedMarketGroupMap.value[marketGroup] = []
  }

  /**
   * Expand all markets in a group
   * @param {object} options
   * @param {string} options.marketGroup market group name. e.g. 'Goals' | 'Bet Builder' | 'Favourites'
   */
  const expandAllMarkets = (options = {}) => {
    const { marketGroup } = options

    const marketIds = marketGroupIdsMap.value[marketGroup]
    const shouldExpandMarketIds = marketIds.filter((id) => activeMarketIds.value.includes(id)) ?? []

    expandedMarketGroupMap.value[marketGroup] = [...shouldExpandMarketIds]
  }

  /**
   * Reset expanded markets state
   */
  const resetExpandedMarkets = () => {
    expandedMarketGroupMap.value = {}
    marketGroupIdsMap.value = {}
  }

  /**
   * Reset favourite market group
   */
  const resetFavouriteMarketGroup = () => {
    if (marketGroupIdsMap.value['Favourites']) {
      marketGroupIdsMap.value['Favourites'] = []
    }
  }

  /**
   * Toggle market expand
   * @param {object} options
   * @param {string} options.marketGroup market group name. e.g. 'Goals' | 'Bet Builder' | 'Favourites'
   * @param {string} options.marketId market id
   */
  const toggleMarketExpand = (options = {}) => {
    const { marketGroup, marketId } = options

    if (expandedMarketGroupMap.value[marketGroup] == null) {
      expandedMarketGroupMap.value[marketGroup] = [marketId]
      return
    }

    const index = expandedMarketGroupMap.value[marketGroup].indexOf(marketId)
    if (index === -1) {
      expandedMarketGroupMap.value[marketGroup].push(marketId)
      return
    }

    expandedMarketGroupMap.value[marketGroup].splice(index, 1)
  }

  const setMarketActive = (marketId) => {
    if (activeMarketIds.value.includes(marketId)) {
      return
    }

    activeMarketIds.value.push(marketId)
  }

  const setMarketInactive = (marketId) => {
    const index = activeMarketIds.value.indexOf(marketId)
    if (index === -1) {
      return
    }

    activeMarketIds.value.splice(index, 1)
  }

  return {
    expandedMarketGroupMap,
    marketGroupIdsMap,
    activeMarketIds,
    initExpandedMarkets,
    trackMarketExpandStatus,
    initBetBuilderMarkets,
    initFavouriteMarkets,
    untrackMarketExpandStatus,
    collapseAllMarkets,
    expandAllMarkets,
    resetExpandedMarkets,
    resetFavouriteMarketGroup,
    toggleMarketExpand,
    setMarketActive,
    setMarketInactive
  }
}
